import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Film, X, Loader2, CheckCircle, Play, Download, Eye, Plus, ImagePlus, RefreshCw, AlertCircle, Music, Clapperboard } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

const QUALITIES = ['360p', '540p', '720p', '1080p'];
const DURATIONS = [5, 8, 10, 15];
const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4'];

const PixverseV6Node = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [negativePrompt, setNegativePrompt] = useState(data.negativePrompt || '');
  const [quality, setQuality] = useState(data.quality || '540p');
  const [duration, setDuration] = useState(data.duration ?? 5);
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '16:9');
  const [generateAudio, setGenerateAudio] = useState(!!data.generateAudio);
  const [multiClip, setMultiClip] = useState(!!data.multiClip);
  const [seed, setSeed] = useState(data.seed ?? '');
  const [firstFrame, setFirstFrame] = useState(data.firstFrame || null);
  const [lastFrame, setLastFrame] = useState(data.lastFrame || null);
  const [lastPrompt, setLastPrompt] = useState(data.lastPrompt || '');

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openVideoPreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  const connData = useCallback((handleId) => {
    const edge = edges.find(e => e.target === id && e.targetHandle === handleId);
    if (!edge) return null;
    const src = nodes.find(n => n.id === edge.source);
    return src?.data?.hasOutput ? src.data : null;
  }, [edges, nodes, id]);

  const firstFrameConn = connData('inputImage');
  const lastFrameConn = connData('inputLastFrame');
  const effectiveFirst = firstFrameConn || firstFrame;
  const effectiveLast = lastFrameConn || lastFrame;
  const isTransition = !!(effectiveFirst && effectiveLast);

  useEffect(() => {
    updateNodeData(id, {
      prompt, negativePrompt, quality, duration, aspectRatio,
      generateAudio, multiClip, seed, firstFrame, lastFrame,
    });
  }, [prompt, negativePrompt, quality, duration, aspectRatio, generateAudio, multiClip, seed, firstFrame, lastFrame]); // eslint-disable-line

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const pickFrame = useCallback((which) => {
    setSourcePickerHandler((result) => {
      const entry = result.file
        ? { file: result.file, preview: URL.createObjectURL(result.file), name: result.file.name }
        : { file: null, preview: result.url, name: result.fileName, fromGallery: true };
      if (which === 'first') setFirstFrame(entry); else setLastFrame(entry);
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, openSourcePicker]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    const usedPrompt = prompt.trim();
    if (!usedPrompt) return false;
    resetExec();

    const files = [];
    const params = {
      prompt: usedPrompt,
      quality,
      duration: Number(duration),
      generate_audio: generateAudio,
      generate_multi_clip: isTransition ? false : multiClip,
    };
    // El ratio sólo aplica a texto→video: con imagen el modelo lo ignora.
    if (!effectiveFirst) params.aspect_ratio = aspectRatio;
    if (negativePrompt.trim()) params.negative_prompt = negativePrompt.trim();
    const seedNum = parseInt(seed, 10);
    if (seed !== '' && !Number.isNaN(seedNum)) params.seed = seedNum;

    const attachFrame = async (src, key) => {
      if (!src) return;
      let file = src.file;
      if (!file) {
        const url = src.outputUrl || src.preview;
        if (url?.startsWith('http')) { params[key] = url; return; }
        if (url) file = await blobToFile(url, src.name || `${key}.jpg`, 'image/jpeg');
      }
      if (file) files.push({ key, file });
    };
    await attachFrame(effectiveFirst, 'image_url');
    await attachFrame(effectiveLast, 'last_frame_image_url');

    const result = await processNode({
      resultType: 'pixverse_v6',
      files,
      params,
    });

    if (result?.outputUrl) {
      setLastPrompt(usedPrompt);
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'video',
        lastPrompt: usedPrompt,
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'video',
        tool: 'pixverseV6',
        filename: `pixverse_v6_${Date.now()}.mp4`,
        url: result.outputUrl,
        prompt: usedPrompt,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, prompt, negativePrompt, quality, duration, aspectRatio, generateAudio, multiClip, seed,
      effectiveFirst, effectiveLast, isTransition, updateNodeData, addAsset, processNode, resetExec, setReconfigure]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const canProcess = prompt.trim().length > 0;
  const pricey = duration >= 15 || quality === '1080p';

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(prompt)]);

  return (
    <div className={`node-container pixverseV6-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="pixverse-v6-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Input handles: frame inicial / frame final (transición) */}
      <Handle type="target" position={Position.Left} id="inputImage" className="node-handle input-handle" style={{ top: '22%' }} data-testid="pixverse-v6-input-image" />
      <Handle type="target" position={Position.Left} id="inputLastFrame" className="node-handle input-handle" style={{ top: '40%' }} data-testid="pixverse-v6-input-lastframe" />

      {/* Header */}
      <div className="node-header pixverseV6-header">
        <div className="node-header-left">
          <Film size={16} />
          <span>Pixverse V6</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-pixverse-v6">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content pixverseV6-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivos...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Generando video...'}
              </span>
            </div>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result">
            <video
              src={data.outputUrl}
              controls
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'Pixverse V6')}
            />
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openVideoPreview(data.outputUrl, 'Pixverse V6')} data-testid="pixverse-v6-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `pixverse_v6_${Date.now()}.mp4`); }} data-testid="pixverse-v6-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="video" label="pixverseV6" testIdPrefix="pixverse-v6-timeline" />
            </div>
            <div className="nano-regen-section">
              <span className="nano-section-label">Prompt usado:</span>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="pixverseV6"
                label="Prompt de regeneración"
                className="nano-prompt-textarea nano-regen-textarea"
                rows={2}
                testId="pixverse-v6-regen-prompt"
              />
              <button className="nano-regen-btn pixverseV6-regen-btn" onClick={handleRegenerate} data-testid="pixverse-v6-regen-btn">
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="pixverse-v6-reconfigure-btn" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Video creado</span>
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={null}
                onCancel={() => setReconfigure(false)}
                testId="pixverse-v6-reconfigure-notice"
              />
            )}

            {/* Frames inicio / fin (transición) */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <ImagePlus size={12} />
                Frames inicio / fin — opcional
              </span>
              <div className="nano-images-row">
                {effectiveFirst ? (
                  <div className="nano-img-thumb" title="Frame inicial">
                    <img src={effectiveFirst.preview || effectiveFirst.outputUrl} alt="Frame inicial" />
                    {!firstFrameConn && (
                      <button className="nano-clear-btn" onClick={() => setFirstFrame(null)} data-testid="pixverse-v6-clear-first">
                        <X size={8} />
                      </button>
                    )}
                  </div>
                ) : (
                  <button className="nano-add-img-btn" onClick={() => pickFrame('first')} title="Frame inicial (imagen → video)" data-testid="pixverse-v6-add-first">
                    <Plus size={14} />
                  </button>
                )}
                {effectiveFirst && (
                  effectiveLast ? (
                    <div className="nano-img-thumb" title="Frame final (transición)">
                      <img src={effectiveLast.preview || effectiveLast.outputUrl} alt="Frame final" />
                      {!lastFrameConn && (
                        <button className="nano-clear-btn" onClick={() => setLastFrame(null)} data-testid="pixverse-v6-clear-last">
                          <X size={8} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <button className="nano-add-img-btn" onClick={() => pickFrame('last')} title="Frame final (transición inicio → fin)" data-testid="pixverse-v6-add-last">
                      <Plus size={14} />
                    </button>
                  )
                )}
              </div>
              {firstFrameConn && <span className="pixverseV6-conn-note">Frame inicial conectado</span>}
              {lastFrameConn && <span className="pixverseV6-conn-note">Frame final conectado</span>}
              {isTransition && (
                <span className="pixverseV6-hint">Modo transición: el video va del frame inicial al final</span>
              )}
            </div>

            {/* Prompt */}
            <div className="nano-prompt-section">
              <div className="nano-prompt-header">
                <span className="nano-section-label">Prompt *</span>
                <DirectorsTrigger nodeId={id} nodeType="pixverseV6" currentPrompt={prompt} onInsert={setPrompt} />
              </div>
              <PromptField
                value={prompt}
                onChange={setPrompt}
                nodeId={id}
                nodeType="pixverseV6"
                placeholder="Escena, sujetos, emoción, cámara (pan/tilt/zoom/tracking). Multi-shot: 'Shot 1..., Shot 2...'"
                className="nano-prompt-textarea"
                rows={3}
                testId="pixverse-v6-prompt"
              />
              <input
                type="text"
                className="nano-select pixverseV6-negative"
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder="Negative prompt (qué evitar) — opcional"
                data-testid="pixverse-v6-negative-prompt"
              />
            </div>

            {/* Opciones */}
            <div className="nano-options-row pixverseV6-options">
              <div className="nano-option">
                <span className="nano-option-label">Calidad</span>
                <select value={quality} onChange={(e) => setQuality(e.target.value)} className="nano-select" data-testid="pixverse-v6-quality">
                  {QUALITIES.map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Duración</span>
                <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="nano-select" data-testid="pixverse-v6-duration">
                  {DURATIONS.map((d) => (
                    <option key={d} value={d}>{d}s</option>
                  ))}
                </select>
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Ratio</span>
                <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="nano-select" disabled={!!effectiveFirst} data-testid="pixverse-v6-aspect-ratio">
                  {ASPECT_RATIOS.map((ar) => (
                    <option key={ar} value={ar}>{ar}</option>
                  ))}
                </select>
              </div>
            </div>
            {effectiveFirst && (
              <span className="pixverseV6-hint">Con imagen de entrada el ratio lo define la imagen</span>
            )}

            {pricey && (
              <div className="videoSound-warning" data-testid="pixverse-v6-cost-note">
                <AlertCircle size={12} />
                <span>Facturación por segundo: 1080p y/o 15s elevan el costo</span>
              </div>
            )}

            {/* Toggles + seed */}
            <div className="banana2pro-toggles">
              <label className="banana2pro-toggle pixverseV6-toggle" title="Audio generado: música, efectos y diálogos (suma al precio por segundo)">
                <input type="checkbox" checked={generateAudio} onChange={(e) => setGenerateAudio(e.target.checked)} data-testid="pixverse-v6-generate-audio" />
                <Music size={11} />
                <span>Audio</span>
              </label>
              <label className={`banana2pro-toggle pixverseV6-toggle ${isTransition ? 'is-disabled' : ''}`} title="Secuencias multi-shot con transiciones de escena. No disponible en modo transición (frames inicio+fin)">
                <input type="checkbox" checked={multiClip && !isTransition} onChange={(e) => setMultiClip(e.target.checked)} disabled={isTransition} data-testid="pixverse-v6-multi-clip" />
                <Clapperboard size={11} />
                <span>Multi-shot</span>
              </label>
              <input
                type="number"
                className="nano-select pixverseV6-seed nodrag nowheel"
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                placeholder="Seed (vacío = aleatorio)"
                title="Semilla para reproducibilidad"
                data-testid="pixverse-v6-seed"
              />
            </div>

            <button
              className="node-process-btn pixverseV6-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="pixverse-v6-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure
                ? 'Regenerar con cambios'
                : (isTransition ? 'Generar transición' : effectiveFirst ? 'Animar imagen' : 'Generar video')}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

PixverseV6Node.displayName = 'PixverseV6Node';

export default PixverseV6Node;
